import React, { useState, useEffect } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { chatNotificationService, type UnreadMessage } from '../services/chatNotificationService';
import type { CurrentPage } from '../App';

interface ChatNotificationPopupProps {
  onNavigate: (page: CurrentPage) => void;
}

const ChatNotificationPopup: React.FC<ChatNotificationPopupProps> = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState<UnreadMessage[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Initialize service
    chatNotificationService.initialize();

    // Listen for new messages
    const handleNewMessage = (message: UnreadMessage) => {
      setNotifications(prev => [...prev, message]);
      
      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setDismissedIds(prev => new Set([...prev, message.id]));
      }, 5000);
    };

    chatNotificationService.addListener(handleNewMessage);

    // Cleanup
    return () => {
      chatNotificationService.removeListener(handleNewMessage);
    };
  }, []);

  const handleDismiss = (messageId: string) => {
    setDismissedIds(prev => new Set([...prev, messageId]));
  };

  const handleOpenChat = (messageId: string) => {
    setDismissedIds(prev => new Set([...prev, messageId]));
    onNavigate({ page: 'teamChat' });
  };

  // Filter out dismissed notifications
  const visibleNotifications = notifications.filter(n => !dismissedIds.has(n.id));

  if (visibleNotifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {visibleNotifications.map((notification) => (
        <div
          key={notification.id}
          className="bg-white rounded-lg shadow-2xl border-2 border-blue-500 p-4 animate-slide-in-right cursor-pointer hover:shadow-3xl transition-all"
          onClick={() => handleOpenChat(notification.id)}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
              <MessageSquare size={20} className="text-white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm font-bold text-slate-900 truncate">
                  {notification.senderName}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss(notification.id);
                  }}
                  className="flex-shrink-0 p-1 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={16} className="text-slate-500" />
                </button>
              </div>
              
              <p className="text-sm text-slate-700 line-clamp-2 mb-2">
                {notification.message}
              </p>
              
              <p className="text-xs text-blue-600 font-semibold">
                Click to view in Team Chat →
              </p>
            </div>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ChatNotificationPopup;
